import { useState } from 'react';
import { Reveal } from '../components/motion';
import SEO from '../components/SEO';
import { SectionTag, ArrowBtn, PageHero } from '../components/bits';
import { useLead } from '../components/LeadModal';
import { FLAGSHIP_PHASES, DRIVE, DEAL } from '../content/data';

function AssessmentTabs() {
  const [tab, setTab] = useState('drive');
  const model = tab === 'drive' ? DRIVE : DEAL;
  return (
    <div data-testid="assessment-models">
    <SEO title="18-Month Flagship Programme" description="Six phases from digital foundations to entrepreneurship, ending with a capstone portfolio defence." />
      <div className="inline-flex rounded-full border border-navy/15 bg-paper p-1">
        {[['drive', 'DRIVE'], ['deal', 'DEAL']].map(([k, label]) => (
          <button
            key={k}
            data-testid={`assessment-tab-${k}`}
            onClick={() => setTab(k)}
            className={`touch-target rounded-full px-8 py-2.5 font-mono text-sm font-semibold transition-colors duration-300 ${tab === k ? 'bg-navy text-gold' : 'text-navy/60 hover:text-navy'}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {model.map((m, i) => (
          <Reveal key={`${tab}-${m.letter}`} delay={i * 0.05} className="flex items-center gap-5 rounded-3xl border border-navy/10 bg-paper p-7">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-electric font-serif text-2xl italic text-white">{m.letter}</span>
            <div>
              <h3 className="text-base font-semibold tracking-tight">{m.term}</h3>
              <span className="font-mono text-[11px] uppercase tracking-widest text-navy/45">{tab === 'drive' ? 'DRIVE dimension' : 'DEAL step'}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export default function Flagship() {
  const { open } = useLead();
  return (
    <div data-testid="flagship-page">
      <PageHero
        ck="flagship"
        tag="18-Month Flagship Programme"
        lines={<>18 Months. Six Phases. <span className="font-serif italic font-normal text-electric">One Market-Ready Journey.</span></>}
        intro="A structured progression from digital foundations to technical depth, marketing, AI, professional execution, independent income and entrepreneurship."
      >
        <ArrowBtn onClick={() => open('Student Programmes')} variant="primary" testId="flagship-cta-apply">Apply for the Flagship Programme</ArrowBtn>
        <ArrowBtn to="/scholarship" variant="ghostDark" testId="flagship-cta-scholarship">Take the Scholarship Test</ArrowBtn>
      </PageHero>

      {/* Six phases */}
      <section className="bg-paper py-20 sm:py-28">
        <div className="wrap">
          <SectionTag>01 — The Six Phases</SectionTag>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {FLAGSHIP_PHASES.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05} className="group relative overflow-hidden rounded-3xl border border-navy/10 bg-white p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10 sm:p-9">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-electric">{p.months}</span>
                <h3 data-testid={`phase-title-${i}`} className="text-h3 mt-3 font-semibold">{p.title}</h3>
                <ul className="mt-5 grid grid-cols-2 gap-2">
                  {p.items.map((x) => (
                    <li key={x} className="flex items-center gap-2 text-[13px] font-medium text-navy/75">
                      <span className="h-1 w-1 shrink-0 rounded-full bg-electric" />{x}
                    </li>
                  ))}
                </ul>
                <span className="pointer-events-none absolute -bottom-6 -right-3 font-mono text-8xl font-semibold text-navy/[0.04] transition-colors duration-500 group-hover:text-electric/10">0{i + 1}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Assessment */}
      <section className="bg-white py-20 sm:py-28">
        <div className="wrap">
          <SectionTag>02 — Assessment</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 max-w-3xl font-semibold">Your Work Has to <span className="font-serif italic font-normal text-electric">Speak for You.</span></h2></Reveal>
          <Reveal delay={0.08}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/70 sm:text-lg">
              DITEC's curriculum uses practical output, project performance and reflection rather than relying exclusively on conventional written examinations.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="mt-12"><AssessmentTabs /></Reveal>
        </div>
      </section>

      {/* Capstone */}
      <section className="bg-grid-dark bg-navy py-20 text-white sm:py-28">
        <div className="wrap grid items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionTag dark>03 — Capstone</SectionTag>
            <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">Finish With Evidence <span className="font-serif italic font-normal text-gold">You Can Defend.</span></h2></Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-white/70 sm:text-lg">
                At the end of the programme, learners present their complete journey before external industry experts.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="mt-8"><ArrowBtn onClick={() => open('Student Programmes')} variant="gold" testId="flagship-cta-capstone">Understand the Capstone</ArrowBtn></Reveal>
          </div>
          <Reveal delay={0.12}>
            <div className="rounded-3xl border border-gold/20 bg-navy-800/70 p-8 sm:p-10">
              <span className="overline !text-[10px] text-gold/70">The final portfolio brings together</span>
              <ul className="mt-6 space-y-3">
                {['Four digital assets', 'Real projects', 'Commercial activity', 'Analytics', 'Technical work', 'Entrepreneurial strategy'].map((x) => (
                  <li key={x} className="flex items-center gap-3 text-sm font-medium text-white/85 sm:text-base">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />{x}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
